// src/pages/meeting/MeetingRoomManage.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { meetingApi } from "../../../api/meeting";
import BoardTable from "../../../components/common/board/BoardTable";
import PageHeader from "../../../components/common/ui/PageHeader";

// PMIS 공통 스타일 input으로 교체
const inputCls =
  "input-base w-full"; // border/rounded/padding 통합 스타일

export default function MeetingRoomManage() {
  const { id: projectId } = useParams();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const fetchRooms = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await meetingApi.rooms.list({ project: projectId });
      const items = Array.isArray(data?.results) ? data.results : data;
      setRooms(items || []);
    } catch (e) {
      console.error(e);
      setRooms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, [projectId]);

  const addRoom = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await meetingApi.rooms.create({
        project: projectId,
        name,
        order: rooms.length + 1,
      });
      setNewName("");
      fetchRooms();
    } catch (e) {
      const data = e?.response?.data;
      let msg = "회의실 추가에 실패했습니다.";
      if (data?.name) msg = Array.isArray(data.name) ? data.name[0] : data.name;
      alert(msg);
    }
  };

  const updateRoom = async (room, patch) => {
    try {
      await meetingApi.rooms.update(room.id, patch);
      fetchRooms();
      setEditingId(null);
    } catch (e) {
      const data = e?.response?.data;
      let msg = "저장에 실패했습니다.";
      if (data?.name) msg = Array.isArray(data.name) ? data.name[0] : data.name;
      alert(msg);
    }
  };

  const removeRoom = async (room) => {
    const ok = window.confirm(`'${room.name}' 회의실을 삭제할까요?`);
    if (!ok) return;

    try {
      await meetingApi.rooms.remove(room.id);
      fetchRooms();
    } catch (e) {
      alert("삭제에 실패했습니다.");
    }
  };

  const columns = [
    {
      key: "name",
      header: "회의실명",
      render: (room) => {
        const isEditing = editingId === room.id;
        return isEditing ? (
          <input
            className={inputCls}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            autoFocus
          />
        ) : (
          <span>{room.name}</span>
        );
      },
    },
    {
      key: "is_active",
      header: "사용 여부",
      render: (room) => (
        <input
          type="checkbox"
          checked={!!room.is_active}
          onChange={(e) => updateRoom(room, { is_active: e.target.checked })}
        />
      ),
    },
    {
      key: "actions",
      header: "관리",
      render: (room) => {
        const isEditing = editingId === room.id;
        return (
          <div className="flex gap-2 justify-center">
            {isEditing ? (
              <>
                <button
                  className="btn-save-sm"
                  onClick={() => updateRoom(room, { name: editValue.trim() })}
                >
                  저장
                </button>
                <button
                  className="btn-cancel-sm"
                  onClick={() => setEditingId(null)}
                >
                  취소
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn-edit-sm"
                  onClick={() => {
                    setEditingId(room.id);
                    setEditValue(room.name);
                  }}
                >
                  수정
                </button>
                <button
                  className="btn-delete-sm"
                  onClick={() => removeRoom(room)}
                >
                  삭제
                </button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* 페이지 제목 */}
      <PageHeader>회의실 관리</PageHeader>

      {/* 입력 + 추가 영역 (카드형 스타일) */}
      <div className="border rounded-xl bg-white p-4 flex items-center gap-3">
        <input
          className="input-base w-72"
          placeholder="새 회의실명을 입력하세요"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addRoom()}
        />
        <button className="btn-create px-5" type="button" onClick={addRoom}>
          추가
        </button>
      </div>

      {/* 테이블 영역 */}
      <div className="">
        <BoardTable
          columns={columns}
          rows={rooms}
          loading={loading}
          emptyText="등록된 회의실이 없습니다."
        />
      </div>
    </div>
  );
}
